import { cx } from 'class-variance-authority';
import { get } from 'lodash';
import { Controller, useFormContext } from 'react-hook-form';
import useSWR from 'swr';
import { useClubSlug } from '~/components/Clubs/hooks';
import { helperTextClassNames, labelClassNames } from '~/components/ui/Form/StyledInput';

const BILLING_INTERVAL_LABELS: Record<string, string> = {
    one_time: 'einmalig',
    monthly: 'monatlich',
    quarterly: 'vierteljährlich',
    half_yearly: 'halbjährlich',
    yearly: 'jährlich'
};

interface FormItem {
    id: number;
    title: string;
    description?: string;
    price: number;
    unit?: string;
    min_unit?: number;
    max_unit?: number;
    billing_interval?: string;
    active: boolean;
    type?: 'standard' | 'required';
    parent_item_id?: number | null;
    child_mode?: 'auto_included' | 'auto_selected' | 'optional' | null;
    children?: FormItem[];
}

interface FormularPricingTableInputProps {
    name: string;
    label?: string;
    helperText?: string;
    formItemGroupId?: number;
    selectionMode?: 'single' | 'multiple';
    showPriceOverview?: boolean;
    displayLayout?: 'list' | 'columns';
    columnLabelLeft?: string;
    columnLabelRight?: string;
}

const formatPrice = (price: any) => {
    if (isNaN(price) || price === null || price === undefined || price == '0.00') return 'Kostenfrei';
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(price);
};

const ItemCard = ({
    item,
    selected,
    locked,
    type,
    quantity,
    onChange,
    onQuantityChange,
    indent
}: {
    item: FormItem;
    selected: boolean;
    locked?: boolean;
    type: 'checkbox' | 'radio' | 'none';
    quantity?: number;
    onChange: (checked: boolean) => void;
    onQuantityChange?: (quantity: number) => void;
    indent?: boolean;
}) => {
    const hasQuantity = !!item.unit || item.min_unit != null || item.max_unit != null;

    return (
        <div
            className={cx(
                'tw-rounded-xl tw-border tw-border-solid tw-p-3 tw-transition-colors',
                indent && 'tw-ml-6',
                locked && selected
                    ? 'tw-border-accents-3 tw-bg-accents-0'
                    : selected
                      ? 'tw-border-success tw-bg-success/10'
                      : 'tw-border-accents-3 tw-bg-white hover:tw-border-accents-4 hover:tw-bg-accents-0'
            )}
        >
            <label
                className={cx(
                    'tw-mb-0 tw-flex tw-items-center tw-gap-3',
                    locked ? 'tw-cursor-default' : 'tw-cursor-pointer'
                )}
            >
                <div className="tw-mt-0.5 tw-flex tw-size-5 tw-shrink-0 tw-items-center tw-justify-center">
                    {type === 'none' || locked ? (
                        <div
                            className={cx(
                                'tw-grid tw-size-5 tw-grid-cols-1 tw-rounded-md tw-border tw-border-solid',
                                selected ? 'tw-border-accents-4 tw-bg-accents-3' : 'tw-border-accents-2 tw-bg-white'
                            )}
                        >
                            {selected && (
                                <svg
                                    fill="none"
                                    viewBox="0 0 14 14"
                                    className="tw-pointer-events-none tw-col-start-1 tw-row-start-1 tw-size-3.5 tw-self-center tw-justify-self-center tw-stroke-accents-6"
                                >
                                    <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </div>
                    ) : type === 'checkbox' ? (
                        <div
                            className={cx(
                                'tw-grid tw-size-5 tw-grid-cols-1 tw-rounded-md tw-border tw-border-solid tw-transition-colors',
                                selected ? 'tw-border-success tw-bg-success' : 'tw-border-accents-2 tw-bg-white'
                            )}
                        >
                            <input
                                type="checkbox"
                                checked={selected}
                                onChange={(e) => onChange(e.target.checked)}
                                className="tw-col-start-1 tw-row-start-1 tw-appearance-none"
                            />
                            {selected && (
                                <svg
                                    fill="none"
                                    viewBox="0 0 14 14"
                                    className="tw-pointer-events-none tw-col-start-1 tw-row-start-1 tw-size-3.5 tw-self-center tw-justify-self-center tw-stroke-white"
                                >
                                    <path
                                        d="M3 8L6 11L11 3.5"
                                        strokeWidth={2}
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            )}
                        </div>
                    ) : (
                        <div
                            className={cx(
                                'tw-flex tw-size-5 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-transition-colors',
                                selected ? 'tw-border-success tw-bg-success' : 'tw-border-accents-2 tw-bg-white'
                            )}
                        >
                            <input
                                type="radio"
                                checked={selected}
                                onChange={(e) => e.target.checked && onChange(true)}
                                className="tw-appearance-none"
                            />
                            {selected && <span className="tw-block tw-size-2 tw-rounded-full tw-bg-white" />}
                        </div>
                    )}
                </div>
                <div className="tw-min-w-0 tw-flex-1">
                    <span className="tw-block tw-text-sm tw-font-medium tw-text-accents-9">{item.title}</span>
                    {item.description && (
                        <span className="tw-mt-0.5 tw-block tw-text-xs tw-leading-4 tw-text-accents-7">
                            {item.description}
                        </span>
                    )}
                    {locked && (
                        <span className="tw-mt-0.5 tw-block tw-text-[10px] tw-text-accents-5">
                            automatisch enthalten
                        </span>
                    )}
                </div>
                <div className="tw-shrink-0 tw-text-right">
                    <span
                        className={cx(
                            'tw-block tw-text-sm tw-font-semibold',
                            locked && selected
                                ? 'tw-text-accents-6'
                                : selected
                                  ? 'tw-text-success'
                                  : 'tw-text-accents-9'
                        )}
                    >
                        {formatPrice(item.price)}
                        {item.unit && (
                            <span className="tw-ml-1 tw-text-xs tw-font-normal tw-text-accents-6">/ {item.unit}</span>
                        )}
                    </span>
                    {item.billing_interval && item.billing_interval !== 'one_time' && (
                        <span className="tw-block tw-text-[10px] tw-text-accents-6">
                            {BILLING_INTERVAL_LABELS[item.billing_interval]}
                        </span>
                    )}
                </div>
            </label>
            {selected && hasQuantity && onQuantityChange && (
                <div className="tw-mt-3 tw-flex tw-items-center tw-gap-2 tw-border-0 tw-border-t tw-border-solid tw-border-success/30 tw-pt-3">
                    <span className="tw-text-xs tw-font-medium tw-text-accents-7">Anzahl:</span>
                    <input
                        type="number"
                        min={item.min_unit ?? 1}
                        max={item.max_unit ?? undefined}
                        value={quantity ?? item.min_unit ?? 1}
                        onChange={(e) => onQuantityChange(Number(e.target.value))}
                        onClick={(e) => e.stopPropagation()}
                        className="tw-w-20 tw-rounded-lg tw-border tw-border-solid tw-border-accents-3 tw-bg-white tw-px-2 tw-py-1 tw-text-sm focus:tw-border-success focus:tw-outline-none"
                    />
                    {item.unit && <span className="tw-text-xs tw-font-medium tw-text-accents-7">{item.unit}</span>}
                    {item.max_unit && <span className="tw-text-[10px] tw-text-accents-6">(max. {item.max_unit})</span>}
                </div>
            )}
        </div>
    );
};

const INTERVAL_LABELS: Record<string, string> = {
    one_time: 'Einmalig',
    monthly: 'Monatlich',
    quarterly: 'Vierteljährlich',
    half_yearly: 'Halbjährlich',
    yearly: 'Jährlich'
};

const INTERVAL_ORDER = ['one_time', 'monthly', 'quarterly', 'half_yearly', 'yearly'];

const formatPriceCell = (price: number) => (price === 0 ? 'Kostenfrei' : formatPrice(price));

type PricingSelection = { id: string; quantity: number } | string;

const getSelectionId = (v: PricingSelection) => (typeof v === 'string' ? v : v.id);
const getSelectionQuantity = (v: PricingSelection) => (typeof v === 'string' ? 1 : v.quantity);
const hasSelectionQuantity = (item?: FormItem | null) =>
    !!item && (!!item.unit || item.min_unit != null || item.max_unit != null);
const toSelectionEntry = (item: FormItem): PricingSelection =>
    hasSelectionQuantity(item) ? { id: String(item.id), quantity: item.min_unit ?? 1 } : String(item.id);
const collectAutomaticChildSelections = (item: FormItem): PricingSelection[] =>
    (item.children ?? []).flatMap((child) => {
        if (!child.active || !['auto_included', 'auto_selected'].includes(child.child_mode ?? '')) return [];
        return [toSelectionEntry(child), ...collectAutomaticChildSelections(child)];
    });
const normalizeSingleSelections = (value: unknown): PricingSelection[] => {
    if (Array.isArray(value)) return value as PricingSelection[];
    if (typeof value === 'string' && value) return [value];
    if (value && typeof value === 'object' && 'id' in value) return [value as PricingSelection];
    return [];
};

const PriceOverview = ({
    items,
    selections,
    requiredItems
}: {
    items: FormItem[];
    selections: PricingSelection[];
    requiredItems: FormItem[];
}) => {
    // flatten all items (top-level + children) for lookup
    const allItems = items.flatMap((i) => [i, ...(i.children ?? [])]);

    type Row = { title: string; interval: string; price: number; quantity: number };
    const rows: Row[] = [];

    for (const sel of selections) {
        const id = getSelectionId(sel);
        const qty = getSelectionQuantity(sel);
        const item = allItems.find((i) => String(i.id) === id);
        if (item)
            rows.push({
                title: item.title,
                interval: item.billing_interval ?? 'one_time',
                price: item.price,
                quantity: qty
            });
    }
    for (const req of requiredItems) {
        rows.push({ title: req.title, interval: req.billing_interval ?? 'one_time', price: req.price, quantity: 1 });
    }

    if (rows.length === 0) return null;

    const intervals = INTERVAL_ORDER.filter((iv) => rows.some((r) => r.interval === iv));

    const totalPerInterval = Object.fromEntries(
        intervals.map((iv) => [iv, rows.filter((r) => r.interval === iv).reduce((s, r) => s + r.price * r.quantity, 0)])
    );

    return (
        <div className="tw-mt-3 tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-bg-accents-0">
            <table className="tw-w-full tw-text-sm">
                <thead>
                    <tr className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 tw-bg-accents-1">
                        <th className="tw-px-3 tw-py-2 tw-text-left tw-text-xs tw-font-semibold tw-text-accents-6">
                            {' '}
                        </th>
                        {intervals.map((iv) => (
                            <th
                                key={iv}
                                className="tw-px-3 tw-py-2 tw-text-right tw-text-xs tw-font-semibold tw-text-accents-6"
                            >
                                {INTERVAL_LABELS[iv] ?? iv}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr
                            key={i}
                            className="tw-border-0 tw-border-b tw-border-solid tw-border-accents-2 last:tw-border-0"
                        >
                            <td className="tw-px-3 tw-py-1.5 tw-text-accents-7">
                                {r.title}
                                {r.quantity > 1 && (
                                    <span className="tw-ml-1 tw-text-xs tw-text-accents-4">× {r.quantity}</span>
                                )}
                            </td>
                            {intervals.map((iv) => (
                                <td key={iv} className="tw-px-3 tw-py-1.5 tw-text-right tw-font-medium tw-tabular-nums">
                                    {r.interval === iv ? (
                                        <span className={r.price === 0 ? 'tw-text-accents-5' : 'tw-text-accents-8'}>
                                            {formatPriceCell(r.price * r.quantity)}
                                        </span>
                                    ) : (
                                        <span className="tw-text-accents-3">–</span>
                                    )}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
                <tfoot>
                    <tr className="tw-bg-accents-1">
                        <td className="tw-px-3 tw-py-2 tw-font-semibold tw-text-accents-9">Gesamtbetrag</td>
                        {intervals.map((iv) => (
                            <td
                                key={iv}
                                className="tw-px-3 tw-py-2 tw-text-right tw-font-bold tw-tabular-nums tw-text-success"
                            >
                                {formatPriceCell(totalPerInterval[iv])}
                            </td>
                        ))}
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

// Children shown under a selected parent in multiple mode
const ChildrenSection = ({
    children,
    arr,
    onToggleChild,
    onQuantityChange
}: {
    children: FormItem[];
    arr: PricingSelection[];
    onToggleChild: (child: FormItem, checked: boolean) => void;
    onQuantityChange: (childId: string, quantity: number) => void;
}) => {
    if (!children.length) return null;

    return (
        <div className="tw-mt-1.5 tw-space-y-1.5">
            {children.map((child) => {
                const existing = arr.find((v) => getSelectionId(v) === String(child.id));
                const selected = !!existing;
                const locked = child.child_mode === 'auto_included';
                const hasQuantity = !!child.unit || child.min_unit != null || child.max_unit != null;

                return (
                    <ItemCard
                        key={child.id}
                        item={child}
                        selected={selected}
                        locked={locked}
                        type={locked ? 'none' : 'checkbox'}
                        indent
                        quantity={existing ? getSelectionQuantity(existing) : undefined}
                        onChange={(checked) => !locked && onToggleChild(child, checked)}
                        onQuantityChange={
                            hasQuantity && selected && !locked
                                ? (qty) => onQuantityChange(String(child.id), qty)
                                : undefined
                        }
                    />
                );
            })}
        </div>
    );
};

const isFree = (item: FormItem) => Number(item.price) === 0;

const FormularPricingTableInput = ({
    name,
    label,
    helperText,
    formItemGroupId,
    selectionMode = 'multiple',
    showPriceOverview = false,
    displayLayout = 'list',
    columnLabelLeft,
    columnLabelRight
}: FormularPricingTableInputProps) => {
    const {
        control,
        formState: { errors }
    } = useFormContext();
    const clubSlug = useClubSlug();
    const error = get(errors, name);

    const { data, isLoading } = useSWR<{ data: FormItem[] }>(
        formItemGroupId && clubSlug ? `club/${clubSlug}/form-item-group/${formItemGroupId}/form-item` : null
    );

    const allActive = (data?.data ?? []).filter((item) => item.active);
    // Top-level: not a child of another item, not required
    const items = allActive.filter((item) => !item.parent_item_id && item.type !== 'required');
    const requiredItems = allActive.filter((item) => item.type === 'required');

    const useColumns = displayLayout === 'columns';
    const freeItems = useColumns ? items.filter(isFree) : [];
    const paidItems = useColumns ? items.filter((i) => !isFree(i)) : [];

    if (!formItemGroupId) return null;

    return (
        <div className="tw-my-1 tw-w-full">
            {label && <label className={labelClassNames({ isInvalid: !!error })}>{label}</label>}

            <Controller
                control={control}
                name={name}
                defaultValue={[]}
                render={({ field: { value, onChange } }) => {
                    if (isLoading) {
                        return (
                            <div className="tw-space-y-2">
                                {[1, 2, 3].map((i) => (
                                    <div key={i} className="tw-h-14 tw-animate-pulse tw-rounded-xl tw-bg-accents-1" />
                                ))}
                            </div>
                        );
                    }

                    if (items.length === 0) {
                        return <p className="tw-text-sm tw-text-accents-5">Keine Optionen verfügbar.</p>;
                    }

                    if (selectionMode === 'single') {
                        const selections = normalizeSingleSelections(value);
                        const selectedId = selections[0] ? getSelectionId(selections[0]) : '';
                        const selectedItem = items.find((i) => String(i.id) === selectedId);
                        const selectedEntry = selectedItem
                            ? selections.find((entry) => getSelectionId(entry) === String(selectedItem.id))
                            : undefined;
                        const selectedQuantity = selectedEntry ? getSelectionQuantity(selectedEntry) : 1;
                        const hasQuantity = hasSelectionQuantity(selectedItem);

                        const updateSingleQuantity = (itemId: string, quantity: number) => {
                            onChange(
                                selections.map((entry) =>
                                    getSelectionId(entry) === itemId ? { id: itemId, quantity } : entry
                                )
                            );
                        };

                        const toggleSingleChild = (child: FormItem, checked: boolean) => {
                            const childId = String(child.id);
                            if (checked) {
                                if (selections.some((entry) => getSelectionId(entry) === childId)) return;
                                onChange([...selections, toSelectionEntry(child)]);
                                return;
                            }

                            onChange(selections.filter((entry) => getSelectionId(entry) !== childId));
                        };

                        return (
                            <div className="tw-space-y-2">
                                {items.map((item) => {
                                    const isSelected = selectedId === String(item.id);
                                    const itemChildren = item.children?.filter((c) => c.active) ?? [];

                                    return (
                                        <div key={item.id}>
                                            <ItemCard
                                                item={item}
                                                selected={isSelected}
                                                type="radio"
                                                quantity={isSelected ? selectedQuantity : undefined}
                                                onChange={() => {
                                                    onChange([
                                                        toSelectionEntry(item),
                                                        ...collectAutomaticChildSelections(item)
                                                    ]);
                                                }}
                                                onQuantityChange={
                                                    hasQuantity && isSelected
                                                        ? (qty) => updateSingleQuantity(String(item.id), qty)
                                                        : undefined
                                                }
                                            />
                                            {isSelected && itemChildren.length > 0 && (
                                                <ChildrenSection
                                                    arr={selections}
                                                    onToggleChild={toggleSingleChild}
                                                    onQuantityChange={updateSingleQuantity}
                                                >
                                                    {itemChildren}
                                                </ChildrenSection>
                                            )}
                                        </div>
                                    );
                                })}

                                {showPriceOverview && selections.length > 0 && (
                                    <PriceOverview
                                        items={items}
                                        selections={selections}
                                        requiredItems={requiredItems}
                                    />
                                )}
                            </div>
                        );
                    }

                    const arr = (Array.isArray(value) ? value : []) as PricingSelection[];

                    const updateQuantity = (itemId: string, quantity: number) => {
                        onChange(arr.map((v) => (getSelectionId(v) === itemId ? { id: itemId, quantity } : v)));
                    };

                    const selectParent = (item: FormItem) => {
                        const id = String(item.id);
                        const hasQty = !!item.unit || item.min_unit != null || item.max_unit != null;
                        const entry: PricingSelection = hasQty ? { id, quantity: item.min_unit ?? 1 } : id;
                        const newArr: PricingSelection[] = [...arr, entry];

                        // auto-add auto_included and auto_selected children
                        for (const child of item.children ?? []) {
                            if (!child.active) continue;
                            if (child.child_mode === 'auto_included' || child.child_mode === 'auto_selected') {
                                const childId = String(child.id);
                                if (!newArr.find((v) => getSelectionId(v) === childId)) {
                                    const childHasQty =
                                        !!child.unit || child.min_unit != null || child.max_unit != null;
                                    newArr.push(childHasQty ? { id: childId, quantity: child.min_unit ?? 1 } : childId);
                                }
                            }
                        }
                        onChange(newArr);
                    };

                    const deselectParent = (item: FormItem) => {
                        const childIds = new Set((item.children ?? []).map((c) => String(c.id)));
                        onChange(
                            arr.filter((v) => {
                                const id = getSelectionId(v);
                                return id !== String(item.id) && !childIds.has(id);
                            })
                        );
                    };

                    const toggleChild = (child: FormItem, checked: boolean) => {
                        const childId = String(child.id);
                        if (checked) {
                            const hasQty = !!child.unit || child.min_unit != null || child.max_unit != null;
                            const entry: PricingSelection = hasQty
                                ? { id: childId, quantity: child.min_unit ?? 1 }
                                : childId;
                            onChange([...arr, entry]);
                        } else {
                            onChange(arr.filter((v) => getSelectionId(v) !== childId));
                        }
                    };

                    const renderMultipleItems = (subset: FormItem[]) =>
                        subset.map((item) => {
                            const existing = arr.find((v) => getSelectionId(v) === String(item.id));
                            const selected = !!existing;
                            const hasQuantity = !!item.unit || item.min_unit != null || item.max_unit != null;
                            const activeChildren = item.children?.filter((c) => c.active) ?? [];
                            const visibleChildren = selected ? activeChildren : [];

                            return (
                                <div key={item.id}>
                                    <ItemCard
                                        item={item}
                                        selected={selected}
                                        type="checkbox"
                                        quantity={existing ? getSelectionQuantity(existing) : undefined}
                                        onChange={(checked) => {
                                            if (checked) selectParent(item);
                                            else deselectParent(item);
                                        }}
                                        onQuantityChange={
                                            hasQuantity && selected
                                                ? (quantity) => updateQuantity(String(item.id), quantity)
                                                : undefined
                                        }
                                    />
                                    {visibleChildren.length > 0 && (
                                        <ChildrenSection
                                            arr={arr}
                                            onToggleChild={toggleChild}
                                            onQuantityChange={updateQuantity}
                                        >
                                            {visibleChildren}
                                        </ChildrenSection>
                                    )}
                                </div>
                            );
                        });

                    return (
                        <div className="tw-space-y-4">
                            {useColumns ? (
                                <div className="tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2">
                                    <div className="tw-space-y-2">
                                        {columnLabelLeft && (
                                            <p className="tw-mb-1 tw-text-sm tw-font-semibold tw-text-accents-8">
                                                {columnLabelLeft}
                                            </p>
                                        )}
                                        {freeItems.length > 0 ? (
                                            renderMultipleItems(freeItems)
                                        ) : (
                                            <p className="tw-text-sm tw-text-accents-4">—</p>
                                        )}
                                    </div>
                                    <div className="tw-space-y-2">
                                        {columnLabelRight && (
                                            <p className="tw-mb-1 tw-text-sm tw-font-semibold tw-text-accents-8">
                                                {columnLabelRight}
                                            </p>
                                        )}
                                        {paidItems.length > 0 ? (
                                            renderMultipleItems(paidItems)
                                        ) : (
                                            <p className="tw-text-sm tw-text-accents-4">—</p>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                renderMultipleItems(items)
                            )}
                            {showPriceOverview && (arr.length > 0 || requiredItems.length > 0) && (
                                <PriceOverview items={items} selections={arr} requiredItems={requiredItems} />
                            )}
                        </div>
                    );
                }}
            />

            {(error?.message || helperText) && (
                <span className={helperTextClassNames({ isInvalid: !!error })}>
                    {(error?.message as string) || helperText}
                </span>
            )}
        </div>
    );
};

export default FormularPricingTableInput;
